/**
 * Working out what the pointer is over.
 *
 * Priority runs smallest target first -- junction, then opening, then wall, then
 * room -- because a click near a corner almost always means the corner, and the
 * wall underneath it is the easier thing to hit deliberately.
 */

import { closestPointOnSegment, distance, pointInPolygon } from '../model/geometry';
import { openingEnds, wallEnds } from '../model/graph';
import type { Room } from '../model/rooms';
import type { Level, Point } from '../model/types';
import type { Selection } from './render';

export interface HitTestOptions {
  level: Level;
  rooms: Room[];
  point: Point;
  /** Pick radius in model millimetres, derived from a pixel tolerance. */
  toleranceMm: number;
}

export function hitTest(options: HitTestOptions): Selection | null {
  const junction = nearestJunction(options);
  if (junction) return { kind: 'junction', id: junction };

  const opening = nearestOpening(options);
  if (opening) return { kind: 'opening', id: opening };

  const wall = nearestWall(options);
  if (wall) return { kind: 'wall', id: wall };

  const room = options.rooms.find((candidate) => pointInPolygon(options.point, candidate.polygon));
  return room ? { kind: 'room', id: room.id } : null;
}

export function nearestJunction({ level, point, toleranceMm }: HitTestOptions): string | null {
  let best: { id: string; distance: number } | null = null;
  for (const junction of level.junctions) {
    const d = distance(point, junction);
    if (d <= toleranceMm && (!best || d < best.distance)) best = { id: junction.id, distance: d };
  }
  return best?.id ?? null;
}

export function nearestWall({ level, point, toleranceMm }: HitTestOptions): string | null {
  let best: { id: string; distance: number } | null = null;
  for (const wall of level.walls) {
    const ends = wallEnds(level, wall);
    if (!ends) continue;
    // Half the wall's own thickness counts as "on the wall", so thick walls are
    // no harder to hit than thin ones.
    const reach = toleranceMm + wall.thickness / 2;
    const d = closestPointOnSegment(point, ends.a, ends.b).distance;
    if (d <= reach && (!best || d < best.distance)) best = { id: wall.id, distance: d };
  }
  return best?.id ?? null;
}

export function nearestOpening({ level, point, toleranceMm }: HitTestOptions): string | null {
  let best: { id: string; distance: number } | null = null;
  for (const opening of level.openings) {
    const ends = openingEnds(level, opening);
    if (!ends) continue;
    const d = closestPointOnSegment(point, ends.a, ends.b).distance;
    if (d <= toleranceMm && (!best || d < best.distance)) best = { id: opening.id, distance: d };
  }
  return best?.id ?? null;
}

/** Bounding box of everything on a level, for "zoom to fit". */
export function levelBounds(
  level: Level,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (level.junctions.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const junction of level.junctions) {
    minX = Math.min(minX, junction.x);
    minY = Math.min(minY, junction.y);
    maxX = Math.max(maxX, junction.x);
    maxY = Math.max(maxY, junction.y);
  }
  return { minX, minY, maxX, maxY };
}
